/* eslint-disable @typescript-eslint/no-explicit-any */

import { INVOICE_ITEMS } from '../../core/constants/Billing';
import IndexedList from '../shared/IndexedList';
import { Market } from '../../types/shared/Market';
import { MarketPrice, Tier } from '../../types/shared/Tier';
import {
  Billing,
  Field,
  MarketDates,
  VendorAddons,
} from '../../types/shared/Vendor';
import { Addon } from '../../types/shared/Addon';
import formatCurrency from '../../core/support/formatCurrency';
import {
  MultichoiceElementDefinitionRuntype,
  Option,
  OptionIdsRuntype,
} from '../../runtypes/SignupFormDefinitionRuntype';

import { APP_FIELDS } from './constants/Constants';
import { calculateAddonDates } from './calculateAddonDates';
import { getAllPaymentLineItems, getTotals } from './billing';

type OptionsMap = Map<string, Option>;
export type OptionFields = Map<string, OptionsMap>;

// This is used only for quickbooks/freshbooks
type SimpleLineItem = {
  name: string;
  amount: number;
  taxPercent: number;
};

type AddonCallbackParams = {
  market: Market;
  addon: Addon;
  dates: Array<string>;
};

export type PriceResult = {
  lineItems: Array<SimpleLineItem>;
  totalPrice: number;
};

export type ChoiceWithCharge = {
  charge: number | null | undefined;
  operator: string | null | undefined;
  name: string;
  value: string;
  id: string;
};

const { ID_FIELD_MARKETS } = APP_FIELDS;
const emptyMarkets: MarketDates = {};
const CHARGE_REGEX = / (\+|-)\d+(\.\d+)?$/;

function isMatch(formFieldValue: unknown, expectedValue: unknown) {
  if (Array.isArray(formFieldValue)) {
    return formFieldValue.indexOf(expectedValue) !== -1;
  } else {
    return expectedValue === formFieldValue;
  }
}

export function getMatchingPriceTier(
  fields: IndexedList<Field>,
  priceTiers: Array<Tier>,
): Tier | undefined {
  let defaultTier;
  let selectedTier;
  for (let tier of priceTiers) {
    if (tier.id === '0') {
      defaultTier = tier;
      continue;
    }
    if (tier.form_field_id != null) {
      let criteriaField = fields.getByID(tier.form_field_id);
      if (
        criteriaField &&
        isMatch(criteriaField.value, tier.form_field_value)
      ) {
        selectedTier = tier;
        break;
      }
    }
  }
  return selectedTier || defaultTier;
}

export function getMarkets(
  markets: IndexedList<Market>,
  fields: IndexedList<Field>,
) {
  let marketField = fields.getByID(ID_FIELD_MARKETS);
  let marketFieldValue: any = marketField ? marketField.value : null;
  let result: {
    [id: string]: Array<string>;
  } = {};
  let selectedMarkets: MarketDates = marketFieldValue || emptyMarkets;
  for (let [id, selectedDates] of Object.entries(selectedMarkets)) {
    let marketDef = markets.getByID(id);
    if (marketDef) {
      let availableDates = new Set(marketDef.dates);

      let availableSelectedDates = selectedDates.filter((date) =>
        availableDates.has(date),
      );
      if (availableSelectedDates.length) {
        result[id] = availableSelectedDates;
      }
    }
  }
  return result;
}

function calculateSimpleMarketPrice(params: {
  markets: IndexedList<Market>;
  fields: IndexedList<Field>;
  priceTier: Tier;
  selectedMarketDates?: MarketDates;
  addons: Map<string, Addon> | null | undefined;
  // Hacky: The only reason why we have this parameter is because calculatePrice
  // is computing line items as well as price, which it probably shouldn't.
  addonCallback?: (_: AddonCallbackParams) => void;
}) {
  let {
    markets,
    fields,
    priceTier,
    selectedMarketDates,
    addons,
    addonCallback,
  } = params;
  let addonFields = fields
    .filter((field: any) => field?.value?.addonId)
    .getList();

  let selectedMarkets = selectedMarketDates ?? getMarkets(markets, fields);
  let result = 0;
  for (let [marketId, marketDates] of Object.entries(selectedMarkets)) {
    result += priceTier.price_per_date * marketDates.length;
    let market = markets.getByID(marketId);
    if (market && addons) {
      for (let addonField of addonFields) {
        let addonValue = addonField.value as VendorAddons;
        let { addonId } = addonValue;
        let addonDates = calculateAddonDates(addonValue, market, fields);
        let addon = addons.get(addonId);
        if (addon && addonDates.length) {
          addonCallback?.({ market, addon, dates: addonDates });
          let pricePerDate = addon.defaultPrice ?? 0;
          result += pricePerDate * addonDates.length;
        }
      }
    }
  }

  return result;
}

function calculateAdvancedMarketPrice(params: {
  markets: IndexedList<Market>;
  fields: IndexedList<Field>;
  priceTier: Tier;
  selectedMarketDates?: MarketDates;
  addons: Map<string, Addon> | null | undefined;
  // Hacky: The only reason why we have this parameter is because calculatePrice
  // is computing line items as well as price, which it probably shouldn't.
  addonCallback?: (_: AddonCallbackParams) => void;
}) {
  let {
    markets,
    fields,
    priceTier,
    selectedMarketDates,
    addons,
    addonCallback,
  } = params;
  let addonFields = fields
    .filter((field: any) => field?.value?.addonId)
    .getList();
  let selectedMarkets = selectedMarketDates ?? getMarkets(markets, fields);
  let result = 0;
  for (let [marketId, marketDates] of Object.entries(selectedMarkets)) {
    let marketPrice = priceTier.markets[marketId];
    if (marketPrice) {
      result += calculateBulk(marketDates.length, marketPrice);
    }
    let market = markets.getByID(marketId);
    if (market && addons) {
      for (let addonField of addonFields) {
        let addonValue = addonField.value as VendorAddons;
        let { addonId } = addonValue;
        let addonDates = calculateAddonDates(addonValue, market, fields);
        let addon = addons.get(addonId);
        if (addon && addonDates.length) {
          addonCallback?.({ market, addon, dates: addonDates });
          let pricePerDate = addon.defaultPrice ?? 0;
          result += pricePerDate * addonDates.length;
        }
      }
    }
  }
  return result;
}

export function calculateBulk(
  dateCount: number,
  marketPrice: MarketPrice,
  /** Use this if the number of date you want to charge for are less than total registered dates */
  registeredDateCount?: number,
) {
  let largestBulkMatch;
  for (let bulkPrice of marketPrice.bulk) {
    if ((registeredDateCount ?? dateCount) >= bulkPrice.min_date_count) {
      if (
        !largestBulkMatch ||
        bulkPrice.min_date_count > largestBulkMatch.min_date_count
      ) {
        largestBulkMatch = bulkPrice;
      }
    }
  }
  let perDate = largestBulkMatch
    ? largestBulkMatch.per_date
    : marketPrice.per_date;
  return dateCount * perDate;
}

function calculatePrice(
  markets: IndexedList<Market>,
  fields: IndexedList<Field>,
  priceTiers: Array<Tier>,
  addons: Map<string, Addon> | null | undefined,
  optionFields: OptionFields,
): PriceResult {
  // Note: lineItems is only used when sending quickbooks/freshbooks invoices.
  // TODO: We probably shouldn't be computing lineItems in this function.
  let lineItems: Array<SimpleLineItem> = [];
  let priceTier = getMatchingPriceTier(fields, priceTiers);
  if (!priceTier) {
    return { lineItems, totalPrice: 0 };
  }
  let totalPrice = priceTier.base_price;
  let taxPercent = priceTier.tax_rate / 100;

  let basePriceTax = priceTier.base_price_is_tax_exempt ? 0 : taxPercent;
  totalPrice += totalPrice * basePriceTax;
  lineItems.push({
    name: INVOICE_ITEMS.membership_fee,
    amount: priceTier.base_price / 100,
    taxPercent: basePriceTax,
  });

  let addonLineItems: Array<SimpleLineItem> = [];
  let addonCallback = ({ market, addon, dates }: AddonCallbackParams) => {
    addonLineItems.push({
      // Note: This description isn't totally consistent with what we use elsewhere.
      name: `Addon: "${addon.name}" for market "${market.name}" [${dates.length} date(s)]`,
      amount: addon.defaultPrice * dates.length,
      taxPercent,
    });
  };

  let marketPrice = priceTier.simple_pricing
    ? calculateSimpleMarketPrice({
        markets,
        fields,
        priceTier,
        addons,
        addonCallback,
      })
    : calculateAdvancedMarketPrice({
        markets,
        fields,
        priceTier,
        addons,
        addonCallback,
      });

  let additionalCharge = calculateAdditionalCharge(fields, optionFields);

  let selectedMarkets = getMarkets(markets, fields);
  for (let marketDates of Object.values(selectedMarkets)) {
    marketPrice += additionalCharge * marketDates.length;
  }
  totalPrice = totalPrice + marketPrice + marketPrice * taxPercent;
  lineItems.push({
    name: INVOICE_ITEMS.market_charges,
    amount: marketPrice / 100,
    taxPercent,
  });
  return { lineItems, totalPrice: Math.round(totalPrice) / 100 };
}

type TierSimple = {
  id: string;
  tax_rate: number;
  base_price_is_tax_exempt: boolean;
};

export function getTaxPercent(priceTier: TierSimple | null | undefined) {
  if (!priceTier) {
    return 0;
  }
  let taxPercent = priceTier?.tax_rate ?? 0;
  let basePriceTax =
    priceTier?.base_price_is_tax_exempt == null ? 0 : taxPercent;
  return basePriceTax;
}

function totalChargeFromValue(operator: string | null | undefined) {
  return (accumulator: number, addedNumber: number) => {
    switch (operator) {
      case '+':
        return accumulator + addedNumber;
      case '-':
        return accumulator - addedNumber;
      default:
        return accumulator + addedNumber;
    }
  };
}

export function calculateAdditionalCharge(
  fields: IndexedList<Field>,
  optionFields: OptionFields,
): number {
  let fieldList = fields.getList();
  let totalCharge = 0;
  for (let field of fieldList) {
    const fieldValue = field.value;
    if (!OptionIdsRuntype.guard(fieldValue)) {
      continue;
    }
    const optionsMap = optionFields.get(field.id);
    if (!optionsMap) {
      continue;
    }
    let options = [];
    for (const id of fieldValue) {
      const option = optionsMap.get(id);
      if (option) {
        options.push(option);
      }
    }

    let charges = parseChargeFromValue(options);
    for (let eachCharge of charges) {
      if (typeof eachCharge.charge === 'number') {
        totalCharge = totalChargeFromValue(eachCharge.operator)(
          totalCharge,
          Math.round(eachCharge.charge * 100),
        );
      }
    }
  }
  return totalCharge;
}

export function formatChoiceWithCharge(
  name: string,
  charge: number | null | undefined,
  currency = 'USD',
  operator: string | null | undefined,
): string {
  if (charge == null || operator == null) {
    return name;
  } else {
    return `${name} (${operator}${formatCurrency(charge * 100, currency)})`;
  }
}

export function pricingBreakdown(
  fields: IndexedList<Field>,
  priceTier: Tier,
  currency: string,
) {
  let fieldList = fields.getList();
  let chargeString = `Base Price (+${formatCurrency(
    priceTier.price_per_date,
    currency,
  )})`;
  for (let field of fieldList) {
    if (!MultichoiceElementDefinitionRuntype.guard(field)) {
      continue;
    }

    let optionsWithCharge = parseChargeFromValue(field.options);
    optionsWithCharge.forEach(({ name, charge, operator = '+' }) => {
      if (charge) {
        chargeString += `, ${formatChoiceWithCharge(
          name,
          charge,
          currency,
          operator,
        )}`;
      }
    });
  }
  return chargeString;
}

export function parseChargeFromValue(
  options: Array<Option>,
): Array<ChoiceWithCharge> {
  return options
    .filter((option) => !option.deleted)
    .map((option) => {
      let name = option.name;
      let charge;
      let operator;
      let chargeMatch = name.match(CHARGE_REGEX);
      if (chargeMatch && chargeMatch[0]) {
        charge = Number(chargeMatch[0].slice(2));
        operator = chargeMatch[1];
        name = name.substring(0, option.name.indexOf(chargeMatch[0]));
      }
      return {
        value: option.name,
        name,
        charge,
        operator,
        id: option.id,
      };
    });
}

// TODO: Remove this function; it's effectively an alias for getTotals()
export function getRemainingBalance(billing: Billing | undefined) {
  const paymentLineItems = getAllPaymentLineItems(billing?.invoices ?? []);
  const billingSummaryItems = [
    ...(billing?.invoicedItems ?? []),
    ...paymentLineItems,
  ];
  let { remainingBalance } = getTotals(billingSummaryItems);
  return remainingBalance;
}

export default calculatePrice;
